import React, { useContext, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';

import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip
} from 'chart.js';
import { TEACHER_ASSIGNMENT_V2_INSIGHTS_URL } from 'config/urls';
import { isNil } from 'lodash';
import { isEmpty } from 'lodash';

import EmptyDataState from 'pages/Teachers/Insights/components/EmptyDataState';
import LoadingSkeleton from 'pages/Teachers/Insights/components/LoadingSkeleton';
import LoadingState from 'pages/Teachers/Insights/components/LoadingState';
import MasteryLevelGroup from 'pages/Teachers/Insights/components/MasteryLevelGroup';
import { colors } from 'theme/palette';
import {
  trackMixpanelEvent,
  trackMixpanelPageView
} from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';

import TrackerInstanceSectionContext from 'components/TrackerInstanceSectionContext';
import UserContext from 'components/UserContext';

import { ReactComponent as InsufficientIcon } from './assets/insufficient-icon.svg';
import styles from './styles.module.scss';

// Splits a single string label into multiple lines based on a specified maximum line length.
const splitLabelIntoMultiline = (label, maxLineLength) => {
  const chunks = [];

  const labelParts = label.split(/[, ]+/);
  labelParts.forEach((labelPart) => {
    if (isEmpty(chunks)) chunks.push(labelPart);
    else {
      const lastElement = chunks.pop();
      const newElement = `${lastElement} ${labelPart}`;

      if (newElement.length <= maxLineLength) {
        chunks.push(newElement);
      } else {
        chunks.push(lastElement);
        chunks.push(labelPart);
      }
    }
  });

  return chunks;
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MisconceptionsBarChart = ({ barChartData }) => {
  const data = {
    labels: barChartData.map((item) =>
      splitLabelIntoMultiline(item.name.split('(')[0], 15)
    ),
    datasets: [
      {
        label: 'Misconceptions',
        backgroundColor: [
          colors.blue1,
          colors.blue2,
          colors.blue3,
          colors.blue4,
          colors.blue5,
          colors.blue6,
          colors.blue7,
          colors.blue8,
          colors.blue9
        ],
        borderRadius: 10,
        data: barChartData.map((item) => item.students.length)
      }
    ]
  };

  const options = {
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const index = context.dataIndex;
            const percentage = barChartData[index].student_percentage;

            return `${(percentage * 100).toFixed(0)}%`;
          },
          title: function () {
            return '';
          }
        }
      }
    },
    scales: {
      y: {
        ticks: {
          beginAtZero: true,
          callback: function (value) {
            return Number.isInteger(value) ? value : null;
          }
        }
      }
    },
    aspectRatio: 9
  };

  return <Bar data={data} options={options} />;
};

const MisconceptionsOverview = ({
  data,
  isLoading,
  refetchOverview,
  fullyPreAnnotated,
  hasSubmittedStudentWorks
}) => {
  const user = useContext(UserContext);
  const { class_id, standards } = useContext(TrackerInstanceSectionContext);

  useEffect(() => {
    trackMixpanelPageView(user.id, {
      feature: 'insights',
      view: 'misconceptions',
      standards,
      class_id,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
    });
  }, [user, class_id, standards]);

  const trackMisconceptionsMasteryLevelGroupExpand = ({ groupName }) => {
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'insights',
        view: 'misconceptions',
        button: groupName,
        standards,
        class_id,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
      }
    );
  };

  const trackMisconceptionsStudentWorkDialogClick = ({ groupName }) => {
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'insights',
        view: 'misconceptions',
        button: 'student work',
        group: groupName,
        standards,
        class_id,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
      }
    );
  };

  const trackMisconceptionsNextStepsMarkAsCompleted = ({
    completedNextStep
  }) => {
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`,
      {
        feature: 'insights',
        view: 'misconceptions',
        button: 'complete',
        next_step: completedNextStep,
        standards,
        class_id,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
      }
    );
  };

  if (isLoading && isNil(data)) return <LoadingSkeleton />;

  if (
    hasSubmittedStudentWorks &&
    hasSubmittedStudentWorks['has_submitted_student_works'] === false
  ) {
    return <EmptyDataState withoutStudentWorks={true} />;
  }

  if (!isLoading && isEmpty(data) && fullyPreAnnotated) {
    return <EmptyDataState />;
  }

  if (!isLoading && isEmpty(data)) {
    return <LoadingState />;
  }

  const sortedMisconceptionData = data.sort(
    (a, b) => b.student_percentage - a.student_percentage
  );

  return (
    <div className={styles.container}>
      <MisconceptionsBarChart barChartData={sortedMisconceptionData} />
      {sortedMisconceptionData.map(
        (
          { name, student_percentage, students, next_steps, exemplar_urls },
          index
        ) => {
          return (
            <MasteryLevelGroup
              key={index}
              title={name}
              icon={<InsufficientIcon />}
              students={students}
              percentage={student_percentage}
              nextSteps={next_steps}
              exemplarUrls={exemplar_urls}
              refetchOverview={refetchOverview}
              trackGroupExpandEvent={() =>
                trackMisconceptionsMasteryLevelGroupExpand({ groupName: name })
              }
              trackStudentWorkDialogClickEvent={() =>
                trackMisconceptionsStudentWorkDialogClick({ groupName: name })
              }
              trackNextStepCompleteEvent={
                trackMisconceptionsNextStepsMarkAsCompleted
              }
              isMisconception={true}
            />
          );
        }
      )}
    </div>
  );
};

export default MisconceptionsOverview;
